import { useCallback, useId, useMemo, useRef, useState } from 'react';
import { useContentQuery } from './ContentQueryContext';
import styles from './Facets.module.scss';
import CtaButton from 'components/helpers/Atoms/Cta/CtaButton';
import { groupBy } from 'lodash';
import { SvgIcon } from 'components/helpers/SvgIcon';
import clsx from 'clsx';
import { FacetGroup, useFacetConfig } from './FacetConfigContext';
import useDictionary from 'lib/hooks/useDictionary';
import { normalizeGuid } from 'lib/utils/string-utils';
import {
  pushSearchFilterEvent,
  pushSearchFilterClearEvent,
  SearchAnalyticsType,
} from 'components/helpers/Analytics/SearchAndFilters/SearchAndFiltersAnalytics';

interface FlatFacet {
  facetId: string;
  valueLabel: string;
  groupLabel: string;
  groupName: string;
  isPinned?: boolean;
  selected: boolean;
  recordCount: number;
}

const buildFacetGroupsFromSelection = ({
  facetConfig,
  selectedFacetIds,
}: {
  facetConfig: { facetGroup: FacetGroup[] };
  selectedFacetIds: Set<string>;
}) => {
  return facetConfig.facetGroup.map((group) => ({
    name: group.groupName,
    facets: group.facets
      .filter((facet) => selectedFacetIds.has(facet.facetId))
      .map((facet) => ({
        name: facet.valueLabel,
        facetId: facet.facetId,
      })),
  }));
};

export const Facets = ({
  isSticky = false,
  searchType = 'Standard',
  searchAnalyticsEnabled = false,
  renderHeading = true,
  renderFacetGroupHeadings = true,
}: {
  isSticky?: boolean;
  searchType?: SearchAnalyticsType;
  searchAnalyticsEnabled?: boolean;
  renderHeading?: boolean;
  renderFacetGroupHeadings?: boolean;
}) => {
  const { query, searchResponse, setQuery } = useContentQuery();
  const { facetConfig } = useFacetConfig();
  const { getDictionaryValue } = useDictionary();
  const searchFacets = searchResponse?.SearchFacets;
  const showFacets = searchFacets && searchFacets.length > 0;

  const filterResultsLabel =
    getDictionaryValue('Components.Search.FilterResults') || 'Filter Results:';
  const clearAllLabel = getDictionaryValue('Components.Search.ClearAll') || 'Clear All';
  const showLessLabel = getDictionaryValue('Components.Search.ShowLess') || 'Show Less';
  const showMoreLabel = getDictionaryValue('Components.Search.ShowMore') || 'Show More';
  const selectedFacetCount = query?.facetGroups?.flatMap((x) => x.facets).length ?? 0;

  const pushFilterClearAnalytics = useCallback(() => {
    if (!searchAnalyticsEnabled) {
      return;
    }

    pushSearchFilterClearEvent({
      searchType,
      siteSearchPhrase: query?.searchTerm ?? '',
    });
  }, [query?.searchTerm, searchAnalyticsEnabled, searchType]);

  const pushFilterAnalytics = useCallback(
    (facet: FlatFacet) => {
      if (!searchAnalyticsEnabled) {
        return;
      }

      pushSearchFilterEvent({
        searchType,
        siteSearchPhrase: query?.searchTerm ?? '',
        facetFilter: [facet.groupLabel],
        facetFilterValue: [facet.valueLabel],
      });
    },
    [query?.searchTerm, searchAnalyticsEnabled, searchType]
  );

  // Local state for hacked search input
  // const [localSearchTerm, setLocalSearchTerm] = useState(query?.searchTerm || '');

  const flatFacets = useMemo(() => {
    const flatFacets: FlatFacet[] = [];
    if (!searchFacets) {
      return flatFacets;
    }
    for (let i = 0; i < facetConfig.facetGroup.length; i++) {
      const facetConfigGroup = facetConfig.facetGroup[i];
      for (let j = 0; j < facetConfigGroup.facets.length; j++) {
        const facetItem = facetConfigGroup.facets[j];
        const facet = searchFacets.find((x) => {
          const searchId = x.Id;
          const configId = facetItem.facetId;

          if (!searchId || !configId) {
            return false;
          }

          const normalizedSearchId = normalizeGuid(searchId) ?? searchId.toLowerCase();
          const normalizedConfigId = normalizeGuid(configId) ?? configId.toLowerCase();

          return normalizedSearchId === normalizedConfigId;
        });

        // Only display facets that have results for the current search at all
        if (!facet) {
          continue;
        }

        const isSelected =
          query?.facetGroups?.some((x) =>
            x.facets.map((y) => y.facetId).includes(facetItem.facetId)
          ) ?? false;

        flatFacets.push({
          facetId: facetItem.facetId,
          valueLabel: facetItem.valueLabel,
          groupName: facetConfigGroup.groupName,
          groupLabel: facetConfigGroup.groupLabel,
          selected: isSelected,
          recordCount: facet.RecordCount ?? 0,
          isPinned: facetItem.isPinned,
        });
      }
    }

    return flatFacets;
  }, [searchFacets, query, facetConfig]);

  const groupedFacets = useMemo(() => groupBy(flatFacets, 'groupLabel'), [flatFacets]);

  const orderedFacetGroupLabels = useMemo(() => {
    const labels = facetConfig.facetGroup
      .map((group) => group.groupLabel)
      .filter((groupLabel) => Boolean(groupedFacets[groupLabel]?.length));

    return labels;
  }, [facetConfig.facetGroup, groupedFacets]);

  const expandedByDefaultGroups = useMemo(() => {
    return facetConfig.facetGroup.filter((x) => x.expandedByDefault).map((x) => x.groupLabel);
  }, [facetConfig.facetGroup]);

  const handleFacetChange = useCallback(
    (facetId: string) => {
      const facet = flatFacets.find((x) => x.facetId === facetId);
      if (!facet) {
        return;
      }

      facet.selected = !facet.selected;
      pushFilterAnalytics(facet);

      const selectedFacetIds = new Set(
        (query?.facetGroups ?? [])
          .flatMap((group) => group.facets)
          .map((selected) => selected.facetId)
      );

      if (facet.selected) {
        selectedFacetIds.add(facet.facetId);
      } else {
        selectedFacetIds.delete(facet.facetId);
      }

      const newFacetGroups = buildFacetGroupsFromSelection({
        facetConfig,
        selectedFacetIds,
      });

      const nextSelectedFacetCount = newFacetGroups.reduce(
        (count, group) => count + group.facets.length,
        0
      );

      if (selectedFacetCount > 0 && nextSelectedFacetCount === 0) {
        pushFilterClearAnalytics();
      }

      setQuery?.({
        ...query,
        facetGroups: nextSelectedFacetCount === 0 ? [] : newFacetGroups,
      });
    },
    [
      facetConfig,
      flatFacets,
      pushFilterAnalytics,
      pushFilterClearAnalytics,
      query,
      selectedFacetCount,
      setQuery,
    ]
  );

  if (!showFacets) {
    return null;
  }
  return (
    <div className={clsx(styles.facets, { [styles.facetsSticky]: isSticky })}>
      <div className={styles.facetsHeader}>
        {renderHeading ? <h2>{filterResultsLabel}</h2> : <span />}
        {selectedFacetCount > 0 && (
          <CtaButton
            onClick={() => {
              pushFilterClearAnalytics();
              setQuery?.({
                ...query,
                facetGroups: [],
              });
            }}
            text={clearAllLabel}
            ctaStyle="outline"
            ctaComponentClass="component-feature-full-color-cta-1-color"
          />
        )}
      </div>
      {orderedFacetGroupLabels.map((group) => {
        const facetGroup = facetConfig.facetGroup.find((x) => x.groupLabel === group);

        if (!facetGroup) {
          return null;
        }

        return (
          <FacetGroupComponent
            key={group}
            facetGroup={facetGroup}
            facets={groupedFacets[group]}
            handleFacetChange={handleFacetChange}
            showLessLabel={showLessLabel}
            showMoreLabel={showMoreLabel}
            expandedByDefault={expandedByDefaultGroups.includes(group)}
            renderHeading={renderFacetGroupHeadings}
          />
        );
      })}
    </div>
  );
};

function FacetGroupComponent({
  facetGroup,
  facets,
  handleFacetChange,
  showLessLabel,
  showMoreLabel,
  expandedByDefault = false,
  renderHeading = true,
}: {
  facetGroup: FacetGroup;
  facets: FlatFacet[];
  handleFacetChange: (facetId: string) => void;
  showLessLabel: string;
  showMoreLabel: string;
  expandedByDefault?: boolean;
  renderHeading?: boolean;
}) {
  const [expanded, setExpanded] = useState(expandedByDefault);
  const [showMore, setShowMore] = useState(false);
  const firstFacetRef = useRef<HTMLInputElement | null>(null);
  const facetsListId = useId();
  const count = facets.filter((x) => x.selected).length;
  if (facets.length === 0) {
    return null;
  }
  const pinnedFacets = facets.filter((x) => x.isPinned);
  const unpinnedFacets = facets.filter((x) => !x.isPinned);
  if (facetGroup.sortOrder === 'alphabetical') {
    unpinnedFacets.sort((a, b) => a.valueLabel.localeCompare(b.valueLabel));
  } else if (facetGroup.sortOrder === 'count') {
    unpinnedFacets.sort((a, b) => b.recordCount - a.recordCount);
  }
  const orderedFacets = [...pinnedFacets, ...unpinnedFacets];
  const facetsToShow = showMore
    ? orderedFacets
    : orderedFacets.slice(0, facetGroup.showMoreThreshold);

  return (
    <div className={styles.facetGroup}>
      {renderHeading ? (
        <h3 className={styles.facetGroupHeading}>
          <button
            type="button"
            className={styles.facetGroupLabel}
            data-facet-group-trigger="true"
            onClick={() => setExpanded(!expanded)}
            aria-expanded={expanded}
            aria-controls={facetsListId}
          >
            <span>
              {facetGroup.groupLabel} {count > 0 && `(${count})`}
            </span>
            <SvgIcon
              className={clsx(styles.toggleIcon, { [styles['toggleIcon--open']]: expanded })}
              fill="currentColor"
              icon={expanded ? 'iconAccordionMinus' : 'iconAccordionPlus'}
              viewBox="0 0 40 40"
              aria-hidden="true"
            />
          </button>
        </h3>
      ) : (
        <button
          type="button"
          className={styles.facetGroupLabel}
          data-facet-group-trigger="true"
          onClick={() => setExpanded(!expanded)}
          aria-expanded={expanded}
          aria-controls={facetsListId}
        >
          <span>
            {facetGroup.groupLabel} {count > 0 && `(${count})`}
          </span>
          <SvgIcon
            className={clsx(styles.toggleIcon, { [styles['toggleIcon--open']]: expanded })}
            fill="currentColor"
            icon={expanded ? 'iconAccordionMinus' : 'iconAccordionPlus'}
            viewBox="0 0 40 40"
            aria-hidden="true"
          />
        </button>
      )}
      {expanded && (
        <div id={facetsListId} className={styles.facetsList}>
          {facetsToShow.map((facet, index) => (
            <FacetValue
              key={facet.facetId}
              facet={facet}
              handleFacetChange={handleFacetChange}
              // When the list is fully expanded, move focus to the first checkbox
              inputRef={showMore && index === 0 ? firstFacetRef : undefined}
            />
          ))}
          {facetGroup.showMoreThreshold && facets.length > facetGroup.showMoreThreshold && (
            <CtaButton
              className={styles.showMoreless}
              onClick={() => {
                const nextShowMore = !showMore;
                setShowMore(nextShowMore);
                // After expanding to show all, shift focus to the first facet
                if (!showMore && nextShowMore) {
                  // Slight delay to ensure DOM is updated
                  setTimeout(() => {
                    firstFacetRef.current?.focus();
                  }, 0);
                }
              }}
              text={showMore ? showLessLabel : showMoreLabel}
              ctaStyle="link"
              ctaComponentClass="component-feature-full-color-cta-1-color"
            />
          )}
        </div>
      )}
    </div>
  );
}

function FacetValue({
  facet,
  handleFacetChange,
  inputRef,
}: {
  facet: FlatFacet;
  handleFacetChange: (facetId: string) => void;
  inputRef?: React.RefObject<HTMLInputElement | null>;
}) {
  const isDisabled = !facet.selected && facet.recordCount === 0;
  const checkboxLabel = `${facet.valueLabel} (${facet.recordCount})`;

  return (
    <div className={styles.facetFilter} key={facet.facetId}>
      <label>
        <input
          className={styles.filterCheckbox}
          type="checkbox"
          ref={inputRef}
          checked={facet.selected}
          disabled={isDisabled}
          aria-label={checkboxLabel}
          onChange={() => handleFacetChange(facet.facetId)}
        />
        <span aria-hidden="true">{checkboxLabel}</span>
      </label>
    </div>
  );
}
